// LRCLIB (lrclib.net): a free, open lyrics database with synced lyrics in LRC
// form and no API key. Looked up by artist, title, album and duration — which
// is exactly what Spotify's now-playing call hands us. Timing is per line;
// some entries carry word timing ("enhanced" LRC), and parseLrc keeps it.

const BASE = 'https://lrclib.net/api'
const CACHE_KEY = 'lrclibCache'
const CACHE_MAX = 80

const readCache = () => { try { return JSON.parse(localStorage.getItem(CACHE_KEY) || '{}') || {} } catch { return {} } }
const writeCache = (key, value) => {
  try {
    const c = readCache()
    c[key] = { ...value, cachedAt: Date.now() }
    const keys = Object.keys(c).sort((a, b) => (c[a].cachedAt || 0) - (c[b].cachedAt || 0))
    while (keys.length > CACHE_MAX) delete c[keys.shift()]
    localStorage.setItem(CACHE_KEY, JSON.stringify(c))
  } catch { /* private mode */ }
}

const shape = (hit) => (hit
  ? { id: hit.id || null, synced: hit.syncedLyrics || '', plain: hit.plainLyrics || '', instrumental: !!hit.instrumental, notFound: false }
  : { id: null, synced: '', plain: '', instrumental: false, notFound: true })

// Exact lookup first (artist + title + album + duration), then a looser search
// that prefers a synced entry closest in length. `cacheKey` is the Spotify
// track id, so a song is fetched once per device.
export async function findLyrics({ artist, title, album, durationSec, cacheKey }) {
  const cached = cacheKey ? readCache()[cacheKey] : null
  if (cached && !cached.notFound) return cached
  let hit = null
  try {
    const p = new URLSearchParams({ artist_name: artist || '', track_name: title || '' })
    if (album) p.set('album_name', album)
    if (durationSec) p.set('duration', String(Math.round(durationSec)))
    const res = await fetch(`${BASE}/get?${p}`)
    if (res.ok) hit = await res.json()
  } catch { /* fall through to search */ }
  if (!hit) {
    try {
      const p = new URLSearchParams({ track_name: title || '', artist_name: artist || '' })
      const res = await fetch(`${BASE}/search?${p}`)
      if (res.ok) {
        const list = await res.json()
        const scored = (Array.isArray(list) ? list : [])
          .map((x) => ({ x, score: (x.syncedLyrics ? 0 : 1000) + (durationSec ? Math.abs((x.duration || 0) - durationSec) : 0) }))
          .sort((a, b) => a.score - b.score)
        hit = (scored[0] && scored[0].score < 1000 + 15) ? scored[0].x : null   // within 15 s, or synced
      }
    } catch { /* nothing */ }
  }
  const out = shape(hit)
  if (cacheKey) writeCache(cacheKey, out)
  return out
}

// ── LRC parsing ──────────────────────────────────────────────────────────────
// "[mm:ss.xx] text" — several stamps may share one line ("[00:12.00][01:30.00]
// chorus"); enhanced LRC puts "<mm:ss.xx>" before words. Returns lines sorted
// by time: { t (ms), text, words: [{ t, text }] | null }.
const LINE_TS = /\[(\d{1,2}):(\d{2})(?:[.:](\d{1,3}))?\]/g
const WORD_TS = /<(\d{1,2}):(\d{2})(?:[.:](\d{1,3}))?>/g
const HAS_WORD_TS = /<\d{1,2}:\d{2}(?:[.:]\d{1,3})?>/
const toMs = (m, s, f) => Number(m) * 60000 + Number(s) * 1000 + (f ? Number((String(f) + '00').slice(0, 3)) : 0)

export function parseLrc(text) {
  const out = []
  String(text || '').split(/\r?\n/).forEach((raw) => {
    const stamps = [...raw.matchAll(LINE_TS)]
    if (!stamps.length) return
    const body = raw.replace(LINE_TS, '').trim()
    let words = null
    if (HAS_WORD_TS.test(body)) {
      words = []
      const parts = body.split(/(<\d{1,2}:\d{2}(?:[.:]\d{1,3})?>)/)
      let t = null
      parts.forEach((part) => {
        const m = part.match(/^<(\d{1,2}):(\d{2})(?:[.:](\d{1,3}))?>$/)
        if (m) { t = toMs(m[1], m[2], m[3]); return }
        part.split(/\s+/).map((w) => w.trim()).filter(Boolean).forEach((w) => words.push({ t: t == null ? null : t, text: w }))
      })
      if (!words.length) words = null
    }
    const plain = body.replace(WORD_TS, ' ').replace(/\s+/g, ' ').trim()
    stamps.forEach((m) => out.push({ t: toMs(m[1], m[2], m[3]), text: plain, words }))
  })
  out.sort((a, b) => a.t - b.t)
  return out
}

// Index of the line playing at `ms` (the last one that has started), or -1.
export const lineAt = (lines, ms) => {
  let i = -1
  for (let k = 0; k < lines.length; k++) { if (lines[k].t <= ms) i = k; else break }
  return i
}
